import { Button, Tooltip } from "@tm/components"
import { useWorkTask } from "@tm/context-distribution"
import { useLocalization } from "@tm/localization"
import { channel } from "@tm/models"
import { BundleComponent } from "@tm/morpheus"
import { PropsWithChildren, useCallback, useEffect } from "react"

type Props = PropsWithChildren<{
    workTaskId: string
    /** Pass the exported function `exportWorkTaskToShell` here. */
    onExport(workTaskId: string): void
}>

/**
 * Use this to check if the export voucher button should be rendered, if it is not `undefined`.
 * Has to be passed to the `onExport` prop of `ShellExportButton`.
 */
export const exportWorkTaskToShell = typeof tmJSEvents !== "undefined" && tmJSEvents ? tmJSEvents.onExportShoppingBasket : undefined

/**
 * Renders the worktask export button for the NEXT Shell.
 * Use this from within other React components.
 */
export function ShellExportButton({ workTaskId, onExport, children }: Props) {
    const handleExport = useCallback(() => {
        onExport(workTaskId)
    }, [workTaskId, onExport])

    useEffect(() => {
        return channel("APP").subscribe("SHORTCUTS", (rez) => {
            if (rez.key === "dms-voucher") {
                handleExport()
            }
        })
    }, [handleExport])

    return <Button onClick={handleExport}>{children}</Button>
}

/**
 * Renders the worktask export button for the NEXT Shell.
 * Do not export and use this. Only through Morpheus by including it in a ui-config.
 */
function ShellExportButtonForMorpheus() {
    const { translate } = useLocalization()
    const { workTaskId } = useWorkTask() ?? {}

    if (!workTaskId || !exportWorkTaskToShell) {
        return null
    }

    return (
        <Tooltip title={translate(12660)}>
            <ShellExportButton workTaskId={workTaskId} onExport={exportWorkTaskToShell}>
                {translate(1209)} {translate(3008)}
            </ShellExportButton>
        </Tooltip>
    )
}

const ShellExportButtonComponent: BundleComponent<void, typeof ShellExportButtonForMorpheus> = {
    name: "shell-export-button",
    component: ShellExportButtonForMorpheus,
}

export default ShellExportButtonComponent
